import React, { useEffect, useRef, useState } from 'react';
import { Mail, Phone, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';

// Interactive New Hope Service page
export default function NewHopeService(): JSX.Element {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'services' | 'fees' | 'faq'>('services');
  const [accordionOpen, setAccordionOpen] = useState<Record<string, boolean>>({});
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [counter, setCounter] = useState({ clinicians: 0, assessments: 0, years: 0 });
  const modalButtonRef = useRef<HTMLButtonElement | null>(null);

  // animate counters on mount
  useEffect(() => {
    let raf: number;
    const start = Date.now();
    const duration = 1200;
  const from = { clinicians: 12, assessments: 1542, years: 20 };
    const tick = () => {
      const t = Math.min(1, (Date.now() - start) / duration);
      setCounter({
        clinicians: Math.round(from.clinicians * t),
        assessments: Math.round(from.assessments * t),
        years: Math.round(from.years * t),
      });
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    if (!modalOpen && modalButtonRef.current) modalButtonRef.current.focus();
  }, [modalOpen]);

  const toggleAccordion = (key: string) => {
    setAccordionOpen((s) => ({ ...s, [key]: !s[key] }));
  };

  const handleContactSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setFormStatus('sending');
    setTimeout(() => setFormStatus('sent'), 900);
  };

  const services = [
    { id: 'adhd', title: 'ADHD Assessment', fee: '£350', desc: 'Clinical interview, rating scales, collateral history and written report.' },
    { id: 'autism', title: 'Autism Assessment', fee: '£450', desc: 'Developmental history, observation and standardised tools where indicated.' },
    { id: 'combined', title: 'Combined ADHD & Autism', fee: '£700', desc: 'Integrated pathway assessing both conditions where clinically indicated.' },
    { id: 'psychiatry', title: 'Psychiatric Assessment', fee: '£300', desc: 'Diagnostic psychiatric assessment, risk review and treatment plan.' },
  ];

  return (
    <main className="min-h-screen bg-gray-50 text-gray-900">
      {/* HERO */}
      <section className="bg-gradient-to-r from-white to-green-50 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-3">New Hope Service</h1>
              <p className="text-lg text-green-700 font-semibold mb-4">ADHD, Autism &amp; Psychiatry Assessments</p>
              <p className="text-base text-gray-700 leading-relaxed mb-6">
                Specialist assessments for children and adults. Structured, compassionate evaluations with
                clear reports to support diagnosis and treatment planning.
              </p>

              <div className="flex gap-4">
                <button
                  ref={modalButtonRef}
                  onClick={() => setModalOpen(true)}
                  className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-3 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400"
                >
                  <Calendar className="w-4 h-4" aria-hidden /> Book &amp; Pay
                </button>

                <a href="#contact" className="inline-flex items-center gap-2 border border-green-600 text-green-700 px-5 py-3 rounded-md hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-400">Enquire</a>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                  <div className="text-2xl font-bold text-green-600">{counter.clinicians}</div>
                  <div className="text-sm text-gray-600">Clinicians</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                  <div className="text-2xl font-bold text-green-600">{counter.assessments}</div>
                  <div className="text-sm text-gray-600">Assessments</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                  <div className="text-2xl font-bold text-green-600">{counter.years}+</div>
                  <div className="text-sm text-gray-600">Years Experience</div>
                </div>
              </div>

              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white p-4 rounded-lg shadow-sm">
                <h4 className="text-sm font-semibold text-gray-900">How it works</h4>
                <ol className="mt-2 text-sm text-gray-700 list-decimal list-inside">
                  <li>Initial brief phone/online triage.</li>
                  <li>Comprehensive assessment appointment.</li>
                  <li>Feedback session and written report.</li>
                </ol>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-10 sm:py-14">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">About New Hope Service</h2>
          <p className="text-gray-700 mb-4">
            We provide comprehensive, multidisciplinary assessments for ADHD, Autism Spectrum Disorder and general psychiatric needs.
            Our team includes psychiatrists, clinical psychologists and specialist allied professionals.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {services.map((s) => (
              <article key={s.id} className="bg-white rounded-lg p-4 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900">{s.title}</h3>
                <p className="text-sm text-gray-700 my-2">{s.desc}</p>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-bold text-gray-900">{s.fee}</div>
                  <a href={`/booking?service=${s.id}`} className="text-sm inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-md" aria-label={`Book ${s.title}`}>Book &amp; Pay</a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* INTERACTIVE TABS & ACCORDION */}
      <section className="bg-white border-t border-b border-gray-200 py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-2 mb-6" role="tablist" aria-label="New Hope tabs">
            <button onClick={() => setActiveTab('services')} className={`px-4 py-2 rounded-md ${activeTab === 'services' ? 'bg-green-600 text-white' : 'bg-gray-50 text-gray-800'}`} role="tab" aria-selected={activeTab==='services'}>Services</button>
            <button onClick={() => setActiveTab('fees')} className={`px-4 py-2 rounded-md ${activeTab === 'fees' ? 'bg-green-600 text-white' : 'bg-gray-50 text-gray-800'}`} role="tab" aria-selected={activeTab==='fees'}>Fees</button>
            <button onClick={() => setActiveTab('faq')} className={`px-4 py-2 rounded-md ${activeTab === 'faq' ? 'bg-green-600 text-white' : 'bg-gray-50 text-gray-800'}`} role="tab" aria-selected={activeTab==='faq'}>FAQ</button>
          </div>

          {activeTab === 'services' && (
            <div>
              <p className="text-gray-700 mb-4">Click a service card above to learn more and book.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {services.map((s) => (
                  <button key={s.id} onClick={() => setActiveTab('fees')} className="text-left p-4 bg-gray-50 rounded-md shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-400">
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{s.title}</h4>
                        <p className="text-sm text-gray-700">{s.desc}</p>
                      </div>
                      <div className="text-sm font-bold text-gray-900">{s.fee}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'fees' && (
            <div>
              <p className="text-gray-700 mb-4">Fees are indicative. Book &amp; Pay to reserve an appointment.</p>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-md">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Service</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Fee</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Book</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {services.map((s) => (
                      <tr key={s.id}>
                        <td className="px-4 py-4 text-sm text-gray-700">{s.title}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{s.fee}</td>
                        <td className="px-4 py-4 text-center">
                          <a href={`/booking?service=${s.id}`} className="inline-block bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-md" aria-label={`Book ${s.title}`}>Book &amp; Pay</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'faq' && (
            <div>
              <div className="space-y-3">
                <div>
                  <button onClick={() => toggleAccordion('q1')} className="w-full text-left p-3 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-green-400">What happens after assessment?</button>
                  {accordionOpen['q1'] && <div className="p-3 text-sm text-gray-700">You will receive a feedback session and a written report with recommendations.</div>}
                </div>
                <div>
                  <button onClick={() => toggleAccordion('q2')} className="w-full text-left p-3 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-green-400">Are trainees involved?</button>
                  {accordionOpen['q2'] && <div className="p-3 text-sm text-gray-700">Trainee clinicians may be involved under direct supervision to support reduced-fee pathways.</div>}
                </div>
                <div>
                  <button onClick={() => toggleAccordion('q3')} className="w-full text-left p-3 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-green-400">Do you accept referrals?</button>
                  {accordionOpen['q3'] && <div className="p-3 text-sm text-gray-700">We accept self-referrals and professional referrals. Please contact the team for guidance.</div>}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* TERMS & CONTACT */}
      <section id="contact" className="py-10 sm:py-14">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Terms &amp; Enquiries</h3>
              <p className="text-gray-700 mb-4">Please review our <a href="/terms-and-conditions" className="text-green-700 underline">Terms &amp; Conditions</a> before booking.</p>

              <div className="space-y-3">
                <a href="mailto:contact@healthcity.org.uk" className="inline-flex items-center gap-2 text-gray-700 hover:text-green-700">
                  <Mail className="w-5 h-5" aria-hidden /> contact@healthcity.org.uk
                </a>
                <a href="tel:+4403338003006" className="inline-flex items-center gap-2 text-gray-700 hover:text-green-700">
                  <Phone className="w-5 h-5" aria-hidden /> 0333 800 3006
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-3">Send an enquiry</h4>
              {formStatus === 'sent' ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-md text-green-700">Thanks — your enquiry has been sent. We will respond within 3 working days.</div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-3 bg-gray-50 p-4 rounded-md" aria-label="New Hope contact form">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Full name</label>
                    <input name="name" type="text" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input name="email" type="email" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Message</label>
                    <textarea name="message" rows={4} required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-green-500 focus:border-green-500" />
                  </div>
                  <div className="flex justify-end">
                    <button type="submit" disabled={formStatus==='sending'} className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-md">
                      {formStatus === 'sending' ? 'Sending...' : 'Send enquiry'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Modal (simple) */}
      {modalOpen && (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setModalOpen(false)} />
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 z-10">
            <h3 className="text-lg font-semibold text-gray-900">Book an appointment</h3>
            <p className="text-sm text-gray-700 mb-4">Select a service to continue to booking and payment.</p>
            <div className="space-y-3">
              {services.map((s) => (
                <a key={s.id} href={`/booking?service=${s.id}`} className="block w-full text-left p-3 bg-gray-50 rounded-md hover:bg-gray-100">{s.title} — <span className="font-semibold">{s.fee}</span></a>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setModalOpen(false)} className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300">Close</button>
            </div>
          </motion.div>
        </div>
      )}
    </main>
  );
}
